(() => {
  const config = window.alhProductProtection || {};

  if (!config.enabled || !config.variantId) return;

  const nativeFetch = window.fetch.bind(window);
  const cartAddUrl = config.cartAddUrl || '/cart/add';
  const cartChangeUrl = config.cartChangeUrl || '/cart/change';
  const pathFor = (url) => {
    try {
      return new URL(String(url || ''), window.location.origin).pathname.replace(/\.js$/, '');
    } catch (_error) {
      return String(url || '').split('?')[0].replace(/\.js$/, '');
    }
  };
  const isCartAddUrl = (url) => pathFor(url) === pathFor(cartAddUrl);
  const jsonUrl = (url) => url.endsWith('.js') ? url : `${url}.js`;
  const acceleratedSelector = '.shopify-payment-button, shopify-accelerated-checkout';
  const legacyAdds = new Map();

  const withoutProtection = (properties = {}) => {
    const clean = { ...properties };
    delete clean._product_protection;
    delete clean._protection_group;
    // Shopify cannot replace a nonempty properties object with an empty object.
    if (Object.keys(clean).length === 0) clean._protection_removed = 'true';
    return clean;
  };

  const createGroup = () => {
    if (window.crypto?.randomUUID) return `alh-${window.crypto.randomUUID()}`;
    return `alh-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  };

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || config.currency || 'USD',
      }).format(cents / 100);
    } catch (_error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const ensureHiddenInput = (form, name, value) => {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.dataset.protectionMetadata = 'true';
      form.appendChild(input);
    }
    input.value = value;
  };

  const removeHiddenInputs = (form) => {
    form.querySelectorAll('[data-protection-metadata="true"]').forEach((input) => input.remove());
  };

  class ProductProtection extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.checkbox = this.querySelector('.alh-product-protection__input');
      this.priceElement = this.querySelector('[data-protection-price]');
      this.statusElement = this.querySelector('[data-protection-status]');
      this.form =
        document.getElementById(this.dataset.formId) ||
        this.closest('form') ||
        (this.dataset.autoPlace === 'true' ? document.querySelector('form.so-product-form') : null);

      if (!this.checkbox || !this.form) return;

      if (this.dataset.autoPlace === 'true') {
        this.dataset.formId = this.form.getAttribute('id') || '';
        const purchaseArea = this.form.closest('#so-product-form, .so-product-form-wrap');
        const anchor =
          this.form.querySelector('.so-btn-add-to-cart-wrap, [data-add-to-cart]') ||
          purchaseArea?.querySelector('.so-btn-add-to-cart-wrap, [data-add-to-cart]');

        if (anchor) {
          anchor.parentNode.insertBefore(this, anchor);
          this.dataset.placed = 'true';
        }
      }

      this.checkbox.addEventListener('change', () => {
        this.clearStatus();
        this.syncFormMetadata();
        this.updatePrice();
      });

      this.acceleratedStates = new Map();
      this.refresh();
      ['change', 'input'].forEach((eventName) => {
        this.form.addEventListener(eventName, (event) => {
          if (event.target.matches('[name="quantity"]')) this.updatePrice();
        });
      });
      this.form.addEventListener('click', (event) => {
        if (this.selected && event.target.closest(acceleratedSelector)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
      this.acceleratedObserver = new MutationObserver(() => this.syncAcceleratedCheckout());
      this.acceleratedObserver.observe(this.form, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden', 'inert', 'style', 'disabled', 'aria-hidden'],
      });
    }

    refresh() {
      this.form = document.getElementById(this.dataset.formId) || this.closest('form');
      this.quantityInput = this.form?.querySelector('[name="quantity"]');
      this.syncFormMetadata();
      this.updatePrice();
    }

    prepareAdd() {
      this.dataset.group = createGroup();
      this.syncFormMetadata();
      return this.dataset.group;
    }

    syncAcceleratedCheckout() {
      if (!this.form || !this.acceleratedStates) return;
      if (!this.selected) {
        this.acceleratedStates.forEach((state, element) => {
          element.hidden = state.hidden;
          if (state.inert) element.setAttribute('inert', '');
          else element.removeAttribute('inert');
          if (state.ariaHidden === null) element.removeAttribute('aria-hidden');
          else element.setAttribute('aria-hidden', state.ariaHidden);
          if (state.display) element.style.setProperty('display', state.display, state.displayPriority);
          else element.style.removeProperty('display');
          state.buttons.forEach((disabled, button) => { button.disabled = disabled; });
        });
        this.acceleratedStates.clear();
        return;
      }
      this.form.querySelectorAll(acceleratedSelector).forEach((element) => {
        if (element.parentElement?.closest(acceleratedSelector)) return;
        if (!this.acceleratedStates.has(element)) {
          this.acceleratedStates.set(element, {
            hidden: element.hidden,
            inert: element.hasAttribute('inert'),
            ariaHidden: element.getAttribute('aria-hidden'),
            display: element.style.getPropertyValue('display'),
            displayPriority: element.style.getPropertyPriority('display'),
            buttons: new Map(),
          });
        }
        const state = this.acceleratedStates.get(element);
        element.querySelectorAll('button, input[type="submit"]').forEach((button) => {
          if (!state.buttons.has(button)) state.buttons.set(button, button.disabled);
          if (!button.disabled) button.disabled = true;
        });
        if (!element.hidden) element.hidden = true;
        if (!element.hasAttribute('inert')) element.setAttribute('inert', '');
        if (element.getAttribute('aria-hidden') !== 'true') element.setAttribute('aria-hidden', 'true');
        if (element.style.getPropertyValue('display') !== 'none' || element.style.getPropertyPriority('display') !== 'important') {
          element.style.setProperty('display', 'none', 'important');
        }
      });
    }

    syncFormMetadata() {
      if (!this.form) return;
      this.form.classList.toggle('is-protection-selected', this.selected);
      this.syncAcceleratedCheckout();

      if (!this.selected) {
        removeHiddenInputs(this.form);
        delete this.dataset.group;
        return;
      }

      if (!this.dataset.group) this.dataset.group = createGroup();
      ensureHiddenInput(this.form, 'properties[_product_protection]', '180-day');
      ensureHiddenInput(this.form, 'properties[_protection_group]', this.dataset.group);
    }

    get selected() {
      return Boolean(this.checkbox?.checked);
    }

    get quantity() {
      return Math.max(1, Number.parseInt(this.quantityInput?.value, 10) || 1);
    }

    updatePrice() {
      if (!this.priceElement) return;
      const unitPrice = Number.parseInt(this.dataset.priceCents, 10) || 0;
      this.priceElement.textContent = `+${formatMoney(unitPrice * this.quantity, this.dataset.currency)}`;
    }

    showWarning(message) {
      if (!this.statusElement) return;
      this.statusElement.textContent = message;
      this.statusElement.hidden = false;
    }

    clearStatus() {
      if (!this.statusElement) return;
      this.statusElement.textContent = '';
      this.statusElement.hidden = true;
    }
  }

  if (!customElements.get('product-protection')) {
    customElements.define('product-protection', ProductProtection);
  }

  const getProperties = (formData) => {
    const properties = {};

    for (const [name, value] of formData.entries()) {
      const match = name.match(/^properties\[(.+)]$/);
      if (!match || value === '') continue;
      if (
        match[1] === '_product_protection' ||
        match[1] === '_protection_group' ||
        match[1] === '_product_protection_item'
      ) {
        continue;
      }
      if (typeof value !== 'string') return null;
      properties[match[1]] = value;
    }

    return properties;
  };

  const addProtectionItem = async ({ quantity, group, protectedVariantId }) => {
    const protectionData = new FormData();
    protectionData.set('id', String(config.variantId));
    protectionData.set('quantity', String(quantity));
    protectionData.set('properties[_product_protection_item]', '180-day');
    protectionData.set('properties[_protection_group]', group);
    protectionData.set('properties[_protected_variant]', String(protectedVariantId));

    const response = await nativeFetch(jsonUrl(cartAddUrl), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: protectionData,
    });
    const data = await response.json();

    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || data.errors || 'Protection add failed');
    }

    return data;
  };

  const removeProtectionMetadata = async (mainItem, originalProperties) => {
    if (!mainItem?.key) throw new Error('The cart item could not be identified.');
    const properties = withoutProtection(originalProperties || mainItem.properties || {});
    const response = await nativeFetch(jsonUrl(cartChangeUrl), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: mainItem.key,
        quantity: mainItem.quantity || 1,
        properties,
      }),
    });
    const result = await response.json();
    if (!response.ok || result.status || result.errors) {
      throw new Error(result.description || result.message || 'Protection metadata could not be removed.');
    }
  };

  const selectorForGroup = (group) =>
    Array.from(document.querySelectorAll('product-protection')).find(
      (selector) => selector.dataset.group === group
    );

  window.fetch = async function protectedFetch(resource, options = {}) {
    const url = typeof resource === 'string' ? resource : resource?.url;
    const body = options?.body;
    const isProtectedMainAdd =
      isCartAddUrl(url) &&
      body instanceof FormData &&
      body.get('properties[_product_protection]') === '180-day' &&
      body.get('properties[_product_protection_item]') !== '180-day';

    if (!isProtectedMainAdd) return nativeFetch(resource, options);

    const previousGroup = body.get('properties[_protection_group]');
    const selector = selectorForGroup(previousGroup);
    const legacyEntry = legacyAdds.get(previousGroup);
    // Every add gets its own group, even when a product form survives browser Back.
    const group = legacyEntry ? previousGroup : (selector?.prepareAdd() || createGroup());
    body.set('properties[_protection_group]', group);
    const protectedVariantId = body.get('id');
    const quantity = Math.max(1, Number.parseInt(body.get('quantity'), 10) || 1);
    const originalProperties = getProperties(body);

    try {
      const response = await nativeFetch(resource, options);
      const result = await response.clone().json().catch(() => null);
      if (!response.ok || result?.status || result?.errors) return response;
      const mainItem = result?.items?.[0] || result;
      try {
        await addProtectionItem({ quantity, group, protectedVariantId });
      } catch (error) {
        console.warn('Product protection could not be added.', error);
        await removeProtectionMetadata(mainItem, originalProperties).catch((cleanupError) => console.warn(cleanupError));
        selector?.showWarning(config.addWarning || 'Protection could not be added. Your item was still added to the cart.');
      }
      return response;
    } finally {
      if (legacyEntry) {
        legacyAdds.delete(previousGroup);
        legacyEntry.deferred.resolve({ protectionHandled: true });
      }
    }
  };

  const requestGroup = (data) => {
    if (data instanceof FormData || data instanceof URLSearchParams) return data.get('properties[_protection_group]');
    if (typeof data === 'string') return new URLSearchParams(data).get('properties[_protection_group]');
    return data?.['properties[_protection_group]'];
  };

  const setupShineOnProtection = () => {
    const $ = window.jQuery;
    if (!$) return;

    $('form.so-product-form').each(function setupForm() {
      const form = this;
      const formId = form.getAttribute('id') || '';
      const selector =
        (formId && document.querySelector(`product-protection[data-form-id="${CSS.escape(formId)}"]`)) ||
        form.querySelector('product-protection');
      if (!selector || $(form).data('alh-protection-ready')) return;
      $(form).data('alh-protection-ready', true);

      form.addEventListener('submit', () => {
        selector.refresh?.();
        const deferred = $.Deferred();
        $(form).trigger('shineon/add_submit_deferred', [deferred.promise()]);
        if (!selector.selected) {
          deferred.resolve({ protectionSkipped: true });
          return;
        }
        const group = selector.prepareAdd();
        legacyAdds.set(group, {
          deferred,
          selector,
          group,
          quantity: selector.quantity,
          protectedVariantId: new FormData(form).get('id'),
          originalProperties: getProperties(new FormData(form)),
        });
      }, true);
    });

    $(document).on('ajaxSuccess.alhProtection', async (_event, _xhr, settings, data) => {
      if (!isCartAddUrl(settings?.url)) return;
      const group = requestGroup(settings?.data);
      const entry = legacyAdds.get(group);
      if (!entry) return;
      legacyAdds.delete(group);
      try {
        if (!data?.status && !data?.errors) await addProtectionItem(entry);
      } catch (error) {
        console.warn('Product protection could not be added to the ShineOn item.', error);
        await removeProtectionMetadata(data, entry.originalProperties).catch((cleanupError) => console.warn(cleanupError));
        entry.selector.showWarning(config.addWarning || 'Protection could not be added. Your item was still added to the cart.');
      } finally {
        entry.deferred.resolve({ protectionHandled: true });
      }
    });

    $(document).on('ajaxError.alhProtection', (_event, _xhr, settings) => {
      if (!isCartAddUrl(settings?.url)) return;
      const group = requestGroup(settings?.data);
      const entry = legacyAdds.get(group);
      if (!entry) return;
      legacyAdds.delete(group);
      entry.deferred.resolve({ protectionSkipped: true });
    });
  };

  const initialize = () => {
    document.querySelectorAll('product-protection').forEach((selector) => selector.refresh?.());
    window.setTimeout(setupShineOnProtection, 0);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  window.addEventListener('pageshow', () => {
    document.querySelectorAll('product-protection').forEach((selector) => selector.refresh?.());
  });
})();
